import React, { useEffect, useState } from 'react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { getAnalytics, ComprehensiveAnalyticsData } from '../../services/analyticsService';

const AnalyticsPage: React.FC = () => {
    const [data, setData] = useState<ComprehensiveAnalyticsData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchAnalytics = async () => {
            try {
                setLoading(true);
                const response = await getAnalytics();
                setData(response);
                setError(null);
            } catch (err: any) {
                setError(err.response?.data?.detail || 'Failed to fetch analytics data.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchAnalytics();
    }, []);

    const PIE_COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AF19FF', '#FF1919'];

    if (loading) {
        return <div>Loading analytics dashboard...</div>;
    }

    if (error) {
        return <div style={{ color: 'red' }}>Error: {error}</div>;
    }

    if (!data) {
        return <div>No analytics data available.</div>;
    }

    return (
        <div className="space-y-6">
            <h1 className="text-3xl font-bold">Website Analytics Dashboard</h1>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                    <CardHeader><CardTitle>Total Users</CardTitle></CardHeader>
                    <CardContent><p className="text-2xl font-bold">{data.summary.totalUsers}</p></CardContent>
                </Card>
                <Card>
                    <CardHeader><CardTitle>New Users</CardTitle></CardHeader>
                    <CardContent><p className="text-2xl font-bold">{data.summary.newUsers}</p></CardContent>
                </Card>
                <Card>
                    <CardHeader><CardTitle>Total Sessions</CardTitle></CardHeader>
                    <CardContent><p className="text-2xl font-bold">{data.summary.sessions}</p></CardContent>
                </Card>
                <Card>
                    <CardHeader><CardTitle>Engagement Rate</CardTitle></CardHeader>
                    <CardContent><p className="text-2xl font-bold">{data.summary.engagementRate}</p></CardContent>
                </Card>
            </div>

            {/* Time Series Chart */}
            <Card>
                <CardHeader><CardTitle>Users and Page Views (Last 28 Days)</CardTitle></CardHeader>
                <CardContent style={{ width: '100%', height: 300 }}>
                    <ResponsiveContainer>
                        <LineChart data={data.timeSeries}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="activeUsers" name="Active Users" stroke="#8884d8" />
                            <Line type="monotone" dataKey="screenPageViews" name="Page Views" stroke="#82ca9d" />
                        </LineChart>
                    </ResponsiveContainer>
                </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Users by Country Chart */}
                <Card>
                    <CardHeader><CardTitle>Top 10 Users by Country</CardTitle></CardHeader>
                    <CardContent style={{ width: '100%', height: 300 }}>
                        <ResponsiveContainer>
                            <BarChart data={data.byCountry} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" />
                                <YAxis type="category" dataKey="country" width={150} />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="users" fill="#8884d8" />
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>

                {/* Traffic Sources Chart */}
                <Card>
                    <CardHeader><CardTitle>Sessions by Traffic Source</CardTitle></CardHeader>
                    <CardContent style={{ width: '100%', height: 300 }}>
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie data={data.byTrafficSource} dataKey="sessions" nameKey="channel" cx="50%" cy="50%" outerRadius={100} fill="#8884d8" label>
                                    {data.byTrafficSource.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default AnalyticsPage;